import { ViewportScroller } from '@angular/common';
import { inject as ngInject } from '@angular/core';
import type { Scroll } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { delay, filter, first, map, mergeMap, switchMap, tap } from 'rxjs/operators';

import { appInject } from 'src/app/app-injector';
import { ScrollingService } from 'src/app/core/services/scrolling/scrolling.service';
import { HEADER_SCROLL_ANCHOR_ID, SEAMLESS_STORE_NAMESPACE } from 'src/app/utils/global-constants';

import { selectIsInIntegratedMode } from '../../common/common.selectors';
import { seamlessStoreFns } from '../../common/common.utils';
import { routerScrollAction } from '../../router/router.actions';
import { selectFilteredPreviousRouteState } from '../../router/router.selectors';

/**
 * Restore or set respective scroll position when navigating.
 *
 * ℹ️ 👉 if region picker (e.g. Australia) is open, we prevent scrolling to still have the region picker (placed in the OneWeb header) in the viewport!
 */
export const scrollRestore$ = createEffect(
  (actions$ = ngInject(Actions)) =>
    actions$.pipe(
      ofType(routerScrollAction),
      concatLatestFrom(() => appInject(Store).select(selectIsInIntegratedMode)),
      switchMap(([routerScrollData, isInIntegratedMode]) => {
        const isRegionPickerAvailable = seamlessStoreFns.isSeamlessStoreNamespaceAvailable(
          SEAMLESS_STORE_NAMESPACE.regionPicker
        );

        if (isInIntegratedMode && isRegionPickerAvailable) {
          return seamlessStoreFns.observeRegionPicker().pipe(
            first(),
            map(regionPickerState => ({ hasOpenedRegionPicker: regionPickerState.isOpen, routerScrollData }))
          );
        }
        // no region picker available
        return of({
          hasOpenedRegionPicker: false,
          routerScrollData
        });
      }),
      filter(({ hasOpenedRegionPicker }) => !hasOpenedRegionPicker),
      mergeMap(({ routerScrollData }) =>
        appInject(Store).pipe(
          selectFilteredPreviousRouteState,
          first(),
          map(previousRoute => ({
            routerState: routerScrollData.routeState,
            event: routerScrollData.event,
            previousRoute
          }))
        )
      ),
      tap(({ event }) => {
        // Delay of 0 forces the scrolling on top of the event loop (after the ui has been rendered).
        // Otherwise the browser might try to scroll before there is any content available.
        of(null)
          .pipe(delay(0))
          .subscribe(() => {
            handleScroll(event as Scroll);
          });
      })
    ),
  { functional: true, dispatch: false }
);

function handleScroll(event: Scroll): void {
  if (event?.position) {
    // backward navigation
    appInject<ViewportScroller>(ViewportScroller).scrollToPosition(event.position);
  } else if (event?.anchor) {
    // if the anchor reference is the header, we dont need to consider the header again.
    if (event.anchor === HEADER_SCROLL_ANCHOR_ID) {
      // anchor navigation
      appInject<ViewportScroller>(ViewportScroller).scrollToAnchor(event.anchor);
    } else appInject<ScrollingService>(ScrollingService).scrollToAnchorWithHeaderConsideration(event.anchor);
  } else {
    // forward navigation
    appInject<ViewportScroller>(ViewportScroller).scrollToPosition([0, 0]);
  }
  appInject<ScrollingService>(ScrollingService).resetActiveFocusOnScrolling();
}
